import { useEffect, type CSSProperties, type ErrorInfo, type ReactNode } from 'react';
import { Link, useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { CloudOff } from 'lucide-react';
import { Button, EmptyState } from '@paige/ui';
import { ApiError } from '../../api/http';
import { useAuth, useAuthStore } from '../../auth/AuthBoundary';
import { SUPPORT_MAILTO, SUPPORT_NAME } from '../../support-contact';
import { StateMark } from './StateMark';

/**
 * What a person is told when a screen cannot be opened, and what a developer is told at the same
 * moment (IMPLEMENTATION.md row 36).
 *
 * Without a boundary the router shows its own screen: "Something went wrong!", a Show Error button
 * and, behind it, the thrown message. That message is whatever raised it — the API's `detail`, the
 * browser's own "Failed to fetch", the URL of a chunk that would not load — so it carries vendor
 * and library names, internal hostnames and build file names in front of a paying customer, which
 * the branding rule forbids (`docs/ui-concept.md`, Branding). Paige says one sentence of its own
 * and nothing else: not in the description, not behind a disclosure, not in a title or data
 * attribute. The error itself goes to the console, where the customer is not looking.
 *
 * The word is *screen*, not *page*: in Paige a page is one scanned or rendered page image
 * (`docs/engineering/glossary.md`).
 */

/** The heading, whoever is reading. Neither cause this catches — Paige not answering, or the
 * browser not fetching what the screen is made of — can be named without guessing wrong. */
const title = 'This screen could not be opened';

/** With a session, support is the way on; `action.contact_support` is already Paige's phrase. */
const signedIn = 'Try again in a moment. If it keeps happening, contact Ademero support.';

/**
 * Without one, support is not theirs to reach, and sign-in is the screen that opens regardless.
 * The sentence names sign-in, so the screen has to carry it: this boundary replaces everything the
 * match had rendered, so on a cold load there is no shell, no mark and no other link on the screen
 * — a visitor told to go to sign-in with nothing to press is at a dead end.
 */
const signedOut = 'Try again in a moment, or go to sign-in.';

/** The empty state gives its action one slot; two controls need a gap and a wrap at 360. */
const controls: CSSProperties = {
  display: 'flex',
  gap: 'var(--paige-space-2)',
  justifyContent: 'center',
  flexWrap: 'wrap',
};

/** Beside Try again: support's address for someone signed in, sign-in for someone who is not. */
function WayOn({ known }: { known: boolean }): ReactNode {
  if (known)
    return (
      <Button asChild variant="ghost">
        <a href={SUPPORT_MAILTO}>Email {SUPPORT_NAME}</a>
      </Button>
    );
  return (
    <Button asChild>
      <Link to="/sign-in" search={{}}>
        Go to sign-in
      </Link>
    </Button>
  );
}

export function ScreenFailure({ error, reset }: ErrorComponentProps): ReactNode {
  const router = useRouter();
  const { status } = useAuth();
  const store = useAuthStore();
  // A company that turned on two-step sign-in while this session had none refuses the screen's
  // own loads (403 mfa_required); that is not a failure to show, it is sign-in's second step.
  // The same rule `main.tsx` applies to a query that fails after the screen is up.
  const secondStep = error instanceof ApiError && error.secondFactorRequired;
  useEffect(() => {
    if (!secondStep) return;
    store.requireSecondFactor();
    const next = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    void router.navigate({ to: '/sign-in', search: { next } });
  }, [secondStep, store, router]);
  if (secondStep) return null;
  // `unknown` is the session that could not be read, which is the failure itself: who they are is
  // not known, so they are offered the screen that opens without knowing.
  const known = status === 'signed-in';
  return (
    <div style={{ padding: 'var(--paige-space-6)' }}>
      <EmptyState
        illustration={<StateMark icon={CloudOff} />}
        title={title}
        description={known ? signedIn : signedOut}
        action={
          <div style={controls}>
            <Button variant="primary" onClick={() => retry(reset, router)}>
              Try again
            </Button>
            <WayOn known={known} />
          </div>
        }
      />
    </div>
  );
}

/**
 * A real retry. `reset` clears this boundary's own state and nothing else, so on the next render
 * the match is still in error and throws the same thing straight back. `invalidate` is the one that
 * re-runs the work: every match whose load failed goes back to pending with its error cleared, and
 * `beforeLoad` and `loader` run again.
 */
function retry(reset: () => void, router: ReturnType<typeof useRouter>): void {
  reset();
  void router.invalidate();
}

/**
 * The half of this the customer never sees. The router calls it for every error a boundary catches,
 * after it has re-thrown a `notFound()`, so nothing here can swallow one.
 */
export function reportFailure(error: unknown, info: ErrorInfo): void {
  console.error('[paige] a screen could not be opened', error, info.componentStack ?? '');
}
